import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import useProvisioningJobsHook from './useProvisioningJobsHook';
import ProvisioningJobTable from './ProvisioningJobTable';
import {
  canProvisionFacility,
  canProvisionLearners,
  computeSwitchToCanvasReadiness,
  summarizeChain,
} from './utils/provisioning-jobs.utils';
import { normalizeDeliveryLms } from './utils/facility-lms.utils';

/**
 * Operational panel for a single facility's Canvas provisioning (CC-14598, §10):
 * provision the facility (and its parent chain), bulk-provision its existing
 * learners, and monitor the resulting jobs. Disabled-states mirror the backend
 * preconditions via provisioning-jobs.utils so the UI never offers an action
 * the server rejects.
 *
 * The "Provision Facility" button uses the **incremental** endpoint
 * (`POST /api/admin/facilities/:id/provision-canvas`) which walks the parent
 * chain top-down, creates a Canvas SLA per link, and queues one
 * `facility_created` message per unprovisioned link. The bulk endpoint
 * (`/lms/locations/bulk-provision`) is intentionally retained but not wired
 * in here — keep it for future fleet-wide rollouts.
 *
 * Renders without an outer border when `embedded` is true — used when hosted
 * inside a Dialog which already supplies the chrome.
 */
const ProvisioningJobsPanel = ({ facility, embedded, onSwitchToCanvas }) => {
  const facilityId = facility?.locationKey ?? null;
  const {
    jobs,
    readiness,
    loading,
    error,
    submitting,
    refresh,
    provisionFacility,
    provisionLearners,
    retry,
  } = useProvisioningJobsHook({ facilityId });

  const facilityGate = useMemo(() => canProvisionFacility(facility, readiness), [facility, readiness]);
  const learnerGate = useMemo(
    () => canProvisionLearners(facility, jobs, readiness),
    [facility, jobs, readiness],
  );
  const switchReadiness = useMemo(
    () => computeSwitchToCanvasReadiness(facility, jobs, readiness),
    [facility, jobs, readiness],
  );
  const chainEntries = useMemo(() => summarizeChain(readiness), [readiness]);
  const alreadyOnCanvas = normalizeDeliveryLms(facility?.currentDeliveryLms) === 'canvas';

  const renderJobs = () => {
    if (loading && jobs.length === 0) {
      return <LinearProgress />;
    }
    if (jobs.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No provisioning jobs for this facility yet.
        </Typography>
      );
    }
    return (
      <ProvisioningJobTable jobs={jobs} submitting={submitting} onRetry={retry} variant="facility" />
    );
  };

  const paperProps = embedded
    ? { elevation: 0, square: true, sx: { p: 0, backgroundColor: 'transparent' } }
    : { elevation: 0, variant: 'outlined', sx: { p: 3, borderRadius: 2 } };

  return (
    <Paper {...paperProps}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight={600}>
          Canvas Provisioning
        </Typography>
        <Tooltip title="Refresh jobs">
          <span>
            <Button
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={refresh}
              disabled={loading}
              aria-label="refresh-jobs"
            >
              Refresh
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || 'Failed to load provisioning jobs.'}
        </Alert>
      )}

      <ChainIndicator entries={chainEntries} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={1.5}>
        <Tooltip title={facilityGate.enabled ? '' : facilityGate.reason}>
          <span>
            <Button
              variant="contained"
              startIcon={<AccountTreeRoundedIcon />}
              disabled={submitting || !facilityGate.enabled}
              onClick={() => provisionFacility(facilityId)}
              data-testid="provision-facility-incremental"
            >
              Provision Facility
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={learnerGate.enabled ? '' : learnerGate.reason}>
          <span>
            <Button
              variant="outlined"
              startIcon={<SchoolRoundedIcon />}
              disabled={submitting || !learnerGate.enabled}
              onClick={() => provisionLearners(facilityId)}
              data-testid="provision-existing-learners"
            >
              Provision Existing Learners
            </Button>
          </span>
        </Tooltip>

        {submitting && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
      </Stack>

      {!learnerGate.enabled && learnerGate.reason && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {learnerGate.reason}
        </Typography>
      )}

      <SwitchToCanvasChecklist
        readiness={switchReadiness}
        alreadyOnCanvas={alreadyOnCanvas}
        onSwitch={onSwitchToCanvas ? () => onSwitchToCanvas(facility) : null}
        submitting={submitting}
      />

      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Provisioning Jobs
      </Typography>

      {renderJobs()}
    </Paper>
  );
};

const ChainEntryIcon = ({ provisioned, slaExists }) => {
  if (provisioned) {
    return <CheckCircleRoundedIcon fontSize="small" color="success" />;
  }
  if (slaExists) {
    return <HourglassEmptyRoundedIcon fontSize="small" color="warning" />;
  }
  return <RadioButtonUncheckedRoundedIcon fontSize="small" color="disabled" />;
};

ChainEntryIcon.propTypes = {
  provisioned: PropTypes.bool.isRequired,
  slaExists: PropTypes.bool.isRequired,
};

const ChainEntryStatus = ({ provisioned, slaExists }) => {
  if (provisioned) return 'Provisioned';
  if (slaExists) return 'Sub-account pending';
  return 'Not yet provisioned';
};

const chainStateSlug = ({ provisioned, slaExists }) => {
  if (provisioned) return 'done';
  if (slaExists) return 'pending';
  return 'todo';
};

/**
 * Renders the parent → facility chain so the operator can see at a glance
 * which locations are already provisioned. Hidden until the readiness
 * payload arrives (we don't want to mis-lead with stale row-level data).
 */
const ChainIndicator = ({ entries }) => {
  if (entries.length === 0) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 3, borderColor: 'divider', borderRadius: 2 }}
      data-testid="facility-chain-indicator"
    >
      <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
        Provisioning chain
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
        Provisioning the facility walks the parent chain top-down — each link
        below must end up provisioned before learners can be migrated.
      </Typography>
      <List dense disablePadding>
        {entries.map((entry) => {
          const status = ChainEntryStatus(entry);
          const stateSlug = chainStateSlug(entry);
          const leafBadge = entry.label === 'Facility' ? 'This facility' : entry.label;
          return (
            <ListItem
              key={entry.key}
              disableGutters
              sx={{ alignItems: 'center', py: 0.5 }}
              data-testid={`chain-item-${entry.key}-${stateSlug}`}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ChainEntryIcon provisioned={entry.provisioned} slaExists={entry.slaExists} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2" fontWeight={entry.provisioned ? 500 : 600}>
                      {entry.name}
                    </Typography>
                    {entry.shortCode && (
                      <Chip size="small" variant="outlined" label={entry.shortCode} sx={{ height: 20 }} />
                    )}
                    <Chip size="small" label={leafBadge} sx={{ height: 20 }} />
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {status}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};

ChainIndicator.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      shortCode: PropTypes.string,
      provisioned: PropTypes.bool.isRequired,
      slaExists: PropTypes.bool.isRequired,
    }),
  ).isRequired,
};

const ChecklistItem = ({ itemKey, done, label, detail }) => (
  <ListItem
    disableGutters
    sx={{ alignItems: 'flex-start', py: 0.5 }}
    data-testid={`switch-to-canvas-checklist-${itemKey}-${done ? 'done' : 'todo'}`}
  >
    <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
      {done ? (
        <CheckCircleRoundedIcon fontSize="small" color="success" />
      ) : (
        <RadioButtonUncheckedRoundedIcon fontSize="small" color="disabled" />
      )}
    </ListItemIcon>
    <ListItemText
      primary={
        <Typography variant="body2" sx={{ fontWeight: done ? 500 : 600, color: done ? 'text.secondary' : 'text.primary' }}>
          {label}
        </Typography>
      }
      secondary={
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      }
    />
  </ListItem>
);

ChecklistItem.propTypes = {
  itemKey: PropTypes.string.isRequired,
  done: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
};

/**
 * Renders the two-step provisioning checklist that gates the operator's
 * "Switch to Canvas" decision. Hidden once the facility is already on
 * Canvas (the row's "Switch to D2L" affordance covers reverse migrations).
 */
const SwitchToCanvasChecklist = ({ readiness, alreadyOnCanvas, onSwitch, submitting }) => {
  if (alreadyOnCanvas) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 3, borderColor: 'divider', borderRadius: 2, backgroundColor: 'grey.50' }}
      data-testid="switch-to-canvas-checklist"
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} gap={2}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            Switch to Canvas readiness
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Complete every step before flipping delivery to Canvas.
          </Typography>
        </Box>
        {onSwitch && (
          <Tooltip title={readiness.ready ? '' : 'Complete every checklist item to enable.'}>
            <span>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<SwapHorizRoundedIcon />}
                disabled={!readiness.ready || submitting}
                onClick={onSwitch}
                data-testid="switch-to-canvas-action"
                sx={{ whiteSpace: 'nowrap' }}
              >
                Switch to Canvas
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <List dense disablePadding>
        {readiness.checklist.map((item) => (
          <ChecklistItem key={item.key} itemKey={item.key} done={item.done} label={item.label} detail={item.detail} />
        ))}
      </List>
    </Paper>
  );
};

SwitchToCanvasChecklist.propTypes = {
  readiness: PropTypes.shape({
    ready: PropTypes.bool.isRequired,
    checklist: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        done: PropTypes.bool.isRequired,
        label: PropTypes.string.isRequired,
        detail: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  alreadyOnCanvas: PropTypes.bool.isRequired,
  onSwitch: PropTypes.func,
  submitting: PropTypes.bool,
};

SwitchToCanvasChecklist.defaultProps = {
  onSwitch: null,
  submitting: false,
};

ProvisioningJobsPanel.propTypes = {
  facility: PropTypes.shape({
    locationKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    locationName: PropTypes.string,
    currentDeliveryLms: PropTypes.string,
    canvasSubAccountId: PropTypes.string,
    parentProvisioned: PropTypes.bool,
  }),
  embedded: PropTypes.bool,
  onSwitchToCanvas: PropTypes.func,
};

ProvisioningJobsPanel.defaultProps = {
  facility: null,
  embedded: false,
  onSwitchToCanvas: null,
};

export default ProvisioningJobsPanel;
